import React, { useMemo, useRef, useEffect } from 'react';
import ChatMessage from '../ChatMessage/ChatMessage';
import { groupMessagesByDate } from '../../../utils';
import { AskRequestComment, User } from '@hone-automation/common';
import { useAuthContext } from '../../../../context/useAuthContext';
import { parseJSON } from 'date-fns';
import { getAttachmentType } from '../common/MessageAttachments/MessageAttachments';
import { isEqual } from 'lodash';
import classNames from 'classnames';
import { extractUsersFromComments } from '../utils/userUtils';

export type Attachment = {
  filename: string;
  attachmentId: string;
  fileType: string | undefined;
};

type AskRequestQueueComment = AskRequestComment & {
  attachments?: Attachment[];
};
interface ChatProps {
  comments?: AskRequestQueueComment[];
  fixedHeight?: boolean;
  mobile?: boolean;
  onCommentUpdate?: (commentId: string, updatedComment: AskRequestComment) => void;
}

type CommentOrientation = 'left' | 'right';

type Comment = {
  id: string;
  user?: User;
  orientation: CommentOrientation;
  showDate: boolean;
  glCode?: boolean;
  date?: Date;
  content: string;
  editable: boolean;
  attachments?: Attachment[];
  createdAt: Date;
  canDelete: boolean;
  canUpdate: boolean;
  edited: boolean;
  isFirstMessage?: boolean;
  isHoneInternalUser?: boolean;
  isInternal?: boolean;
};

function Chat({ comments, fixedHeight = false, mobile = false, onCommentUpdate }: ChatProps) {
  const { isBookkeeperAdmin, userId } = useAuthContext();
  const groupedMessages = useMemo(() => groupMessagesByDate(comments ?? []), [comments]);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  // Extract all unique users from comments (including deactivated ones and tagged users)
  const allUsersFromComments = useMemo(() => extractUsersFromComments(comments), [comments]);

  useEffect(() => {
    // Scroll to the bottom when component mounts or when comments change
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [comments]);

  // Additional effect to ensure scroll to bottom on component mount
  useEffect(() => {
    // Scroll to bottom immediately
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }

    // And also after a short delay to handle any potential DOM updates
    const scrollTimer = setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, []);

  const hasMessages = Object.entries(groupedMessages).length > 0;

  return (
    <div
      className={classNames('chat-block', { 'fixed-height': fixedHeight && hasMessages, 'no-messages': !hasMessages })}
      ref={chatContainerRef}
    >
      {!hasMessages ? (
        <p>No messages yet</p>
      ) : (
        Object.entries(groupedMessages).map(([date, messages], groupIndex) =>
          messages.map((message, idx) => {
            const showDate = idx === 0;
            let orientation: CommentOrientation = 'left';
            const isHoneInternalUser = message.userLocation && message.userLocation.isHoneInternalUser ? true : false;
            if ((isBookkeeperAdmin && isHoneInternalUser) || (!isBookkeeperAdmin && !isHoneInternalUser)) {
              orientation = 'right';
            }
            // Use type assertion to access the visibility property without TypeScript errors
            const messageAny = message as any;
            const isInternal = messageAny.visibility && messageAny.visibility === 'Internal' ? true : false;

            const attachments: Attachment[] | undefined = message.attachments?.map(attachment => ({
              filename: attachment.filename,
              attachmentId: attachment.attachmentId,
              fileType: getAttachmentType(attachment),
            }));

            const comment: Comment = {
              user: message.user,
              orientation: orientation,
              editable: message.user?.userId === userId && (message.canDelete || message.canUpdate),
              date: (typeof message.updatedAt as any) === 'string' ? parseJSON(message.updatedAt) : message.updatedAt,
              showDate,
              content: message.comment,
              attachments: attachments,
              createdAt: message.createdAt,
              id: message.id,
              canDelete: message.canDelete,
              canUpdate: message.canUpdate,
              edited: !isEqual(message.createdAt, message.updatedAt),
              isFirstMessage: idx === 0 && groupIndex === 0,
              isHoneInternalUser: isHoneInternalUser,
              isInternal: isInternal,
            };

            const handleCommentEdit = onCommentUpdate
              ? (id: string, content: string) => {
                  const updatedComment: AskRequestComment = {
                    ...message,
                    comment: content,
                    updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ') as unknown as Date,
                  };
                  onCommentUpdate(id, updatedComment);
                }
              : undefined;

            return (
              <ChatMessage
                key={`chat-${idx}`}
                {...comment}
                mobile={mobile}
                onEdit={handleCommentEdit}
                allUsersFromComments={allUsersFromComments}
              />
            );
          })
        )
      )}
    </div>
  );
}

export default Chat;
